import React from 'react';
import { Box, Paper, Typography } from '@mui/material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { useChartTokens, axisProps, gridProps, tooltipStyle } from '../../../dashboard/chartTokens';
import { formatINR } from '../../../dashboard/primitives';
import { TableEmptyState } from '../../common/pageKit';
import { SalesCohorts } from '../../../types/salesAnalytics';

interface CohortsSectionProps {
  cohorts: SalesCohorts | null;
  loading: boolean;
}

/**
 * New vs repeat revenue by month — 2 series sharing one currency axis
 * (stacked, so the total bar height is total revenue and no dual axis is
 * needed even though "customers" is a different unit; that count only
 * appears in the tooltip, never as a third bar on this axis).
 */
const CohortsSection: React.FC<CohortsSectionProps> = ({ cohorts, loading }) => {
  const t = useChartTokens();

  if (loading || !cohorts) {
    return (
      <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', p: 2.5, mb: 3, minHeight: 320 }} />
    );
  }

  const data = cohorts.months.map((m) => ({
    month: new Date(`${m.month}-01T00:00:00`).toLocaleDateString('en-IN', { month: 'short', year: '2-digit' }),
    New: m.newRevenue,
    Repeat: m.repeatRevenue,
    newCustomers: m.newCustomers,
    repeatCustomers: m.repeatCustomers,
  }));

  return (
    <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', p: 2.5, mb: 3 }}>
      <Typography sx={{ fontWeight: 700, mb: 0.25 }}>Customer Cohorts</Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
        New = a customer's first-ever invoice fell in that month. Repeat = an existing customer invoiced again.
      </Typography>

      {data.length === 0 ? (
        <TableEmptyState title="No invoices in this period" hint="Cohort revenue needs at least one invoice in the selected range." />
      ) : (
        <Box sx={{ height: 300 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} margin={{ top: 4, right: 8, left: -8, bottom: 0 }}>
              <CartesianGrid {...gridProps(t)} />
              <XAxis dataKey="month" {...axisProps(t)} />
              <YAxis {...axisProps(t)} width={64} tickFormatter={(v: number) => formatINR(v, true)} />
              <Tooltip
                {...tooltipStyle(t)}
                formatter={(v: any, name: any, item: any) => {
                  const customers = name === 'New' ? item.payload.newCustomers : item.payload.repeatCustomers;
                  return [`${formatINR(Number(v))} · ${customers} customer${customers === 1 ? '' : 's'}`, name];
                }}
              />
              <Legend wrapperStyle={{ fontSize: 12, color: t.textSecondary }} />
              <Bar dataKey="New" stackId="rev" fill={t.series(0)} radius={[0, 0, 0, 0]} maxBarSize={44} />
              <Bar dataKey="Repeat" stackId="rev" fill={t.series(1)} radius={[4, 4, 0, 0]} maxBarSize={44} />
            </BarChart>
          </ResponsiveContainer>
        </Box>
      )}
    </Paper>
  );
};

export default CohortsSection;
